import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, getStandupsGroupedByDate, generateDailySummary, signOut, checkTeamBotTokens, getSlackInstallUrl } from '../lib/supabase'
import { Tables } from '../types/supabase'
import Header from '../components/Header'
import StatsCard from '../components/StatsCard'
import DailyStandupGroup from '../components/DailyStandupGroup'
import VoiceRecorder from '../components/VoiceRecorder'
import { TeamSettings } from '../components/TeamManagement/TeamSettings'
import NewDashboard from '../components/NewDashboard'
import SkeletonLoader from '../components/SkeletonLoader'
import WelcomeModal from '../components/WelcomeModal'
import { Button } from '../components/ui/button'
import { BillingManagement } from '../components/BillingManagement'
import { getSubscriptionStatus, checkTrialStatus } from '../lib/billing'
import { AlertCircle, Crown, X } from 'lucide-react'

// Type aliases for better readability
type Team = Tables<'teams'>
type StandupResponse = Tables<'standup_responses'>
type TeamRole = 'admin' | 'member'

interface DashboardStats {
  totalStandups: number
  thisWeekStandups: number
  activeUsers: number
  teamHealthScore: number
  standupCompletionRate: number
  voiceMemos?: number
  avgSentiment?: number
  voicePercentage?: number
}

interface TeamDashboardProps {
  teamId: string;
}

interface ExtendedStandupResponse {
  id: string
  date: string
  user: string
  user_id: string
  sentiment: number
  audio_path: string | null
  questions: {
    yesterday: string
    today: string
    blockers: string
  }
}

interface GroupedStandupDay {
  date: string
  standups: Array<{
    name: string
    avatar?: string
    timestamp: string
    completed: string
    planned: string
    blockers?: string
    sentiment?: 'positive' | 'neutral' | 'negative'
    sentimentNumeric?: number
    isVoiceEntry?: boolean
    rawDate?: string
    rawCreatedAt?: string
  }>
  teamSummary?: {
    summary: string
    keyAccomplishments: string[]
    upcomingTasks: string[]
    blockers: string[]
    overallSentiment: 'positive' | 'neutral' | 'negative'
  }
}

const TeamDashboard: React.FC<TeamDashboardProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [botTokenStatus, setBotTokenStatus] = useState<{
    hasBotToken: boolean
    hasAccessToken: boolean
    slackTeamId?: string
  } | null>(null)
  const [showChannelInviteBanner, setShowChannelInviteBanner] = useState(false)
  const [appUninstalledAt, setAppUninstalledAt] = useState<string | null>(null)
  
  // Dashboard data
  const [stats, setStats] = useState<DashboardStats>({
    totalStandups: 0,
    thisWeekStandups: 0,
    activeUsers: 0,
    teamHealthScore: 0,
    standupCompletionRate: 0,
    voiceMemos: 0,
    avgSentiment: 0,
  })
  const [groupedStandups, setGroupedStandups] = useState<GroupedStandupDay[]>([])
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false)
  const [loadingSummaries, setLoadingSummaries] = useState<Set<string>>(new Set())
  const [teamMemberCount, setTeamMemberCount] = useState<number>(1)
  
  // Team settings state
  const [showTeamSettings, setShowTeamSettings] = useState(false)
  const [userRole, setUserRole] = useState<TeamRole>('member')

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [isNewTeam, setIsNewTeam] = useState(false)

  // Billing/trial state
  const [showBilling, setShowBilling] = useState(false)
  const [trialStatus, setTrialStatus] = useState<{
    isTrialExpired: boolean
    isOnTrial: boolean
    daysRemaining: number
    trialEndDate?: Date
  } | null>(null)
  const [showTrialBanner, setShowTrialBanner] = useState(true)

  // Debug effect to log trial status
  useEffect(() => {
    if (trialStatus) {
      console.log('🎯 Trial Status Updated:', {
        isExpired: trialStatus.isTrialExpired,
        isOnTrial: trialStatus.isOnTrial,
        daysRemaining: trialStatus.daysRemaining,
        showBanner: showTrialBanner,
        shouldShowBanner: trialStatus.isTrialExpired || (trialStatus.isOnTrial && trialStatus.daysRemaining <= 3)
      })
    }
  }, [trialStatus, showTrialBanner])

  useEffect(() => {
    const loadTeamData = async () => {
      try {
        console.log('=== TEAM DASHBOARD LOADING ===')
        setLoading(true);
        setError(null);

        const { data: team, error: teamError } = await supabase
          .from('teams')
          .select('id, name, slack_team_id, created_at, standup_config, app_uninstalled_at, plan')
          .eq('id', teamId)
          .single()

        if (teamError) throw new Error(`Failed to load team: ${teamError.message}`)
        if (!team) throw new Error('Team not found')

        setSelectedTeam(team as Team)
        
        // Check trial status
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            console.log('📊 Checking trial status for team:', teamId)
            const subscriptionStatus = await getSubscriptionStatus(teamId, user.id)
            console.log('📊 Subscription status:', subscriptionStatus)
            const trial = checkTrialStatus(subscriptionStatus, team.created_at || undefined)
            console.log('📊 Trial status result:', trial)
            console.log('📊 Trial expired?', trial.isTrialExpired)
            console.log('📊 Days remaining:', trial.daysRemaining)
            setTrialStatus(trial)
          }
        } catch (error) {
          console.error('❌ Error checking trial status:', error)
        }
        
        // Check if app was uninstalled
        if ((team as any).app_uninstalled_at) {
          setAppUninstalledAt((team as any).app_uninstalled_at)
        }
        
        // Check if this is a new team that needs onboarding
        const teamCreatedAt = team.created_at ? new Date(team.created_at) : null
        const threeDaysAgo = new Date()
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3)
        
        const isRecentlyCreated = teamCreatedAt && teamCreatedAt > threeDaysAgo
        // Check if team has no standup config OR has default config that's not enabled
        const hasNoStandupConfig = !team.standup_config || 
          Object.keys(team.standup_config).length === 0 || 
          (team.standup_config as any)?.enabled === false
        
        if (isRecentlyCreated && hasNoStandupConfig) {
          setIsNewTeam(true)
          setShowWelcomeModal(true)
        }
        
        // Get current user's role in the team
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const { data: teamMember } = await supabase
            .from('team_members')
            .select('role')
            .eq('team_id', teamId)
            .eq('auth_user_id', user.id)
            .single()
          
          if (teamMember && teamMember.role) {
            setUserRole(teamMember.role as TeamRole)
          }
        }
        
        // Check bot token status
        try {
          const tokenStatus = await checkTeamBotTokens(team.id)
          setBotTokenStatus(tokenStatus)
          
          // Check if user has dismissed the channel invitation banner
          if (tokenStatus.hasBotToken && (team.standup_config as any)?.standup_channel) {
            // Check localStorage for dismissal preference
            const dismissalKey = `channel_invite_dismissed_${team.id}`
            const isDismissed = localStorage.getItem(dismissalKey) === 'true'
            
            if (!isDismissed) {
              setShowChannelInviteBanner(true)
            }
          }
          
          // Check for billing success and trigger Slack installation if needed
          const urlParams = new URLSearchParams(window.location.search)
          const billingStatus = urlParams.get('billing')
          
          if (billingStatus === 'success' && !tokenStatus.hasBotToken) {
            console.log('User returned from successful checkout but Slack app not installed, triggering installation...')
            
            // Clean up URL first
            const newUrl = window.location.pathname + window.location.hash
            window.history.replaceState({}, '', newUrl)
            
            // Trigger Slack installation
            const installUrl = await getSlackInstallUrl(team.slack_team_id || undefined)
            window.location.href = installUrl
            return // Stop loading as we're redirecting
          }
        } catch (tokenError) {
          console.error('Error checking bot tokens:', tokenError)
        }
        
        await loadTeamDashboardData(team as Team)
      } catch (err) {
        console.error('Dashboard error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [teamId]);

  const loadTeamDashboardData = async (team: Team) => {
    try {
      // Get team members count
      const { data: teamMembers, error: teamMembersError } = await supabase
        .from('team_members')
        .select('*')
        .eq('team_id', team.id)
      
      if (teamMembersError) {
        console.error('Error loading team members:', teamMembersError)
      } else {
        setTeamMemberCount(teamMembers?.length || 1)
      }

      // Get grouped standups
      const groupedStandupData = await getStandupsGroupedByDate(team.id, 14)
      
      // Calculate stats from all standups
      const allStandups = groupedStandupData.flatMap(([date, standups]) => standups)
      const totalStandups = allStandups.length
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
      const thisWeekStandups = allStandups.filter(s => s.created_at && new Date(s.created_at) >= oneWeekAgo).length
      const voiceMemos = allStandups.filter(s => s.audio_path).length
      
      // Calculate actual average sentiment
      const standupsWithSentiment = allStandups.filter(s => s.sentiment !== null && s.sentiment !== undefined)
      const avgSentiment = standupsWithSentiment.length > 0 
        ? standupsWithSentiment.reduce((sum, s) => sum + (s.sentiment || 0), 0) / standupsWithSentiment.length
        : 0
      
      setStats(prevStats => ({
        ...prevStats,
        totalStandups,
        thisWeekStandups,
        voiceMemos,
        avgSentiment,
        voicePercentage: totalStandups > 0 ? (voiceMemos / totalStandups) * 100 : 0
      }))
      
      // Format grouped standups
      const formattedGroupedStandups: GroupedStandupDay[] = groupedStandupData.map(([date, standups]) => {
        const getSentimentDisplay = (sentiment: number): 'positive' | 'neutral' | 'negative' => {
          if (sentiment > 0.2) return 'positive'
          if (sentiment < -0.2) return 'negative'
          return 'neutral'
        }
        
        const formattedStandups = standups.map(standup => {
          // console.log('=== FORMATTING STANDUP DEBUG ===')
          // console.log('Raw standup data:', {
          //   date: standup.date,
          //   created_at: standup.created_at,
          //   id: standup.id,
          //   sentiment: standup.sentiment
          // })
          
          return {
            name: standup.users?.name || 'Unknown User',
            timestamp: new Date(standup.date).toLocaleDateString(),
            completed: (standup.questions as any)?.yesterday || 'N/A',
            planned: (standup.questions as any)?.today || 'N/A',
            blockers: (standup.questions as any)?.blockers && (standup.questions as any)?.blockers !== 'None' ? (standup.questions as any)?.blockers : undefined,
            sentiment: getSentimentDisplay(standup.sentiment || 0),
            sentimentNumeric: standup.sentiment || 0, // Preserve numeric sentiment for mood calculation
            isVoiceEntry: !!standup.audio_path,
            // Add debug info for the date
            rawDate: standup.date,
            rawCreatedAt: standup.created_at || undefined
          }
        })
        
        return {
          date,
          standups: formattedStandups,
          teamSummary: undefined // Will be loaded separately
        }
      })
      
      setGroupedStandups(formattedGroupedStandups)
      
      // Generate summaries for each day (in parallel)
      const summaryPromises = formattedGroupedStandups.map(async (day) => {
        if (day.standups.length > 1) { // Only generate summary for days with multiple standups
          try {
            const summary = await generateDailySummary(team.id, day.date)
            return { date: day.date, summary }
          } catch (error) {
            console.error(`Error generating summary for ${day.date}:`, error)
            return { date: day.date, summary: null }
          }
        }
        return { date: day.date, summary: null }
      })
      
      const summaryResults = await Promise.all(summaryPromises)
      
      // Update grouped standups with summaries
      setGroupedStandups(prev => prev.map(day => {
        const summaryResult = summaryResults.find(s => s.date === day.date)
        return {
          ...day,
          teamSummary: summaryResult?.summary || undefined
        }
      }))
      
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      throw error
    }
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const handleVoiceStandupSubmitted = () => {
    setShowVoiceRecorder(false)
    if (selectedTeam) {
      loadTeamDashboardData(selectedTeam)
    }
  }

  const handleTeamClick = () => {
    setShowTeamSettings(true)
  }

  const handleWelcomeComplete = () => {
    setShowWelcomeModal(false)
    setIsNewTeam(false)
    // Optionally refresh team data to get updated standup_config
    if (selectedTeam) {
      loadTeamDashboardData(selectedTeam)
    }
  }

  const handleWelcomeClose = () => {
    setShowWelcomeModal(false)
    // Don't set isNewTeam to false in case user wants to access onboarding later
  }

  const handleChannelInviteDismiss = () => {
    if (selectedTeam) {
      // Save dismissal preference to localStorage
      const dismissalKey = `channel_invite_dismissed_${selectedTeam.id}`
      localStorage.setItem(dismissalKey, 'true')
      setShowChannelInviteBanner(false)
    }
  }

  if (loading) {
    return <SkeletonLoader />
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center"><p>Error: {error}</p></div>
    )
  }

  if (!selectedTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center"><p>Team not found.</p></div>
    )
  }
  
  // Show team settings if requested
  if (showTeamSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header 
          onLogout={handleLogout} 
          onTeamClick={handleTeamClick}
        />
        
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-6">
            <button
              onClick={() => setShowTeamSettings(false)}
              className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Team Settings</h1>
            <TeamSettings teamId={teamId} userRole={userRole} />
          </div>
        </main>
      </div>
    )
  }

  // Main dashboard view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-[#F2F4ED] to-[#E8EBE3] relative">
      {/* Welcome Modal for new teams */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeClose}
        onComplete={handleWelcomeComplete}
        teamName={selectedTeam.name || 'Your Team'}
      />

      {/* Subtle peaceful background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#F2F4ED]/60 to-[#E8EBE3]/60 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-tr from-[#E8EBE3]/50 to-[#F2F4ED]/50 rounded-full blur-3xl"></div>
      </div>
      
      <Header 
        onLogout={handleLogout} 
        onTeamClick={handleTeamClick}
        teamId={selectedTeam.id}
        teamName={selectedTeam.name || undefined}
        onUpgrade={() => setShowBilling(true)}
        showUpgrade={trialStatus?.isOnTrial || false}
        upgradeUrgent={trialStatus?.isTrialExpired || false}
      />

      {/* Trial Status Banner - Prominent and Clear */}
      {trialStatus && showTrialBanner && (trialStatus.isTrialExpired || (trialStatus.isOnTrial && trialStatus.daysRemaining <= 3)) && (
        <div className={`relative z-20 ${trialStatus.isTrialExpired ? 'bg-gradient-to-r from-red-600 to-red-700' : 'bg-gradient-to-r from-amber-500 to-amber-600'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <div className="flex-shrink-0">
                  {trialStatus.isTrialExpired ? (
                    <AlertCircle className="h-8 w-8 text-white" />
                  ) : (
                    <Crown className="h-8 w-8 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">
                    {trialStatus.isTrialExpired 
                      ? '⏰ Your Trial Has Expired' 
                      : `⚠️ Trial Ends in ${trialStatus.daysRemaining} Day${trialStatus.daysRemaining === 1 ? '' : 's'}`}
                  </h3>
                  <p className="text-white/90 text-sm">
                    {trialStatus.isTrialExpired 
                      ? 'Upgrade now to continue submitting standups and keep your team synced.'
                      : 'Add a payment method now to avoid any interruption to your standups.'}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={() => setShowBilling(true)}
                    className="px-6 py-3 bg-white text-gray-900 font-bold rounded-lg shadow-lg hover:bg-gray-100 transition-all transform hover:scale-105 flex items-center space-x-2"
                  >
                    <Crown className="h-5 w-5" />
                    <span>Upgrade Plan</span>
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowTrialBanner(false)}
                className="ml-4 text-white/80 hover:text-white transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Management Modal */}
      {showBilling && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Upgrade Your Plan</h2>
              <button
                onClick={() => setShowBilling(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <BillingManagement 
                teamId={selectedTeam.id}
                userRole={userRole}
                isOpen={showBilling}
                onClose={() => setShowBilling(false)}
              />
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10">
        {/* App Uninstalled Alert - Highest Priority */}
        {appUninstalledAt && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">⚠️ SyncSimple App Uninstalled from Slack</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>The SyncSimple app was uninstalled from your Slack workspace on {new Date(appUninstalledAt).toLocaleDateString()}. Slack features like slash commands and standup posting are no longer available.</p>
                  </div>
                  <div className="mt-4">
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const installUrl = await getSlackInstallUrl(selectedTeam?.slack_team_id)
                          window.location.href = installUrl
                        } catch (error) {
                          console.error('Failed to get install URL:', error)
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                      </svg>
                      Reinstall SyncSimple to Slack
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bot Installation Alert */}
        {botTokenStatus && !botTokenStatus.hasBotToken && !appUninstalledAt && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800">Slack App Installation Required</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>To use the <code className="bg-yellow-100 px-1 rounded">/standup</code> command in Slack, you need to install the SyncSimple app to your workspace.</p>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={async () => {
                        const installUrl = await getSlackInstallUrl(botTokenStatus.slackTeamId)
                        window.open(installUrl, '_blank', 'noopener,noreferrer')
                      }}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Install Slack App
                      <svg className="ml-2 -mr-0.5 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Channel Invitation Banner */}
        {botTokenStatus?.hasBotToken && showChannelInviteBanner && (selectedTeam?.standup_config as any)?.standup_channel && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="mb-8 p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-indigo-800">💡 Did you know? You can get standup summaries in Slack by inviting SyncSimple to any channel!</h3>
                  <div className="mt-2 text-sm text-indigo-700">
                  </div>
                  <div className="mt-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-indigo-800 mb-2">Quick setup:</p>
                        <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
                          <li>Go to any channel in your Slack workspace</li>
                          <li>Type <code className="bg-indigo-100 px-1 rounded">/invite @SyncSimple</code> and press Enter</li>
                          <li>That's it! Standup summaries will now appear in your channel</li>
                        </ol>
                      </div>
                      <button
                        onClick={handleChannelInviteDismiss}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap"
                      >
                        I've Done This Already
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Setup Reminder for New Teams */}
        {isNewTeam && !showWelcomeModal && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800">Complete Your Team Setup</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Configure your team's standup schedule and AI summaries to get the most out of SyncSimple.</p>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={() => setShowWelcomeModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Complete Setup
                      <svg className="ml-2 -mr-0.5 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {showVoiceRecorder && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="mb-8 p-6 bg-white/80 backdrop-blur-sm rounded-lg shadow-md border border-white/40">
              <h3 className="text-lg font-medium text-slate-700 mb-4">Record Your Standup</h3>
              <VoiceRecorder
                onStandupSubmitted={handleVoiceStandupSubmitted}
                userId="current-user-id" // Replace with actual user ID
                teamId={selectedTeam.id}
                supabaseUrl={import.meta.env.VITE_SUPABASE_URL!}
              />
            </div>
          </div>
        )}

        {/* New Dashboard Design */}
        <NewDashboard 
          teamId={teamId}
          teamName={selectedTeam.name || undefined}
          standupData={groupedStandups.flatMap(day => day.standups.map(standup => ({
            user_name: standup.name,
            completed_work: standup.completed,
            planned_work: standup.planned,
            blockers: standup.blockers,
            created_at: standup.rawCreatedAt || standup.rawDate, // Use the raw date from database
            sentiment: standup.sentimentNumeric // Use the numeric sentiment for mood calculation
          })))}
          teamStats={stats}
          teamMemberCount={teamMemberCount}
          groupedStandups={groupedStandups}
        />
      </main>
    </div>
  )
}

export default TeamDashboard 